import type { TextRunStyle } from '@/lib/text/types/text-run-style'

import {
  CLIPBOARD_FRAGMENT_END,
  CLIPBOARD_FRAGMENT_START,
  PASTE_PARA_ATTR,
} from '@/lib/text/constants/clipboard-html-markers'
import { createRunSpan } from '@/lib/text/run-span'

import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { StyleAccum } from './types/style-accum'
import type { ParagraphItem } from './types/paragraph-item'
import type { ExternalPasteOptions } from './types/external-paste-options'
import { walkNode } from './helpers/walk-clipboard-dom'
import { resolveRun } from './helpers/resolve-clipboard-run'

/**
 * Convert external clipboard HTML (from Word, browser, etc.) to an HTML string of
 * `[data-paste-para]` paragraph divs (the same format `buildClipboardHtmlFromRange` produces),
 * each containing `data-text-run` spans, for `insertStyledRunsFromClipboard` to consume. Block-
 * level elements (`<p>`, `<div>`, etc.) become real paragraph boundaries; `<br>` stays a soft
 * break within its paragraph.
 *
 * Extracts: font-size → pointSize, underline, strikethrough, bold/italic (via font variant
 * reverse lookup when `fonts` is provided), letter-spacing → tracking, line-height → leading,
 * font-family → fontFamilyId (via reverse name lookup when `fonts` provides it, unless
 * `options.forceFontFamilyId` is set — see below). Falls back to `baseStyle` for anything
 * unmappable, including blank paragraphs (no per-run style to inherit). Source paragraph
 * alignment is not extracted — pasted paragraphs take the destination's default.
 *
 * `options.forceFontFamilyId`, `options.forceFontId` and `options.forcePointSize` are "the
 * destination decides the typeface, the font style and the size" (WMPCM-7028 AC2: *retains its
 * format, but the font style and size match the spread settings*; WMPCM-13346). When set they
 * replace the clipboard's own font-family / bold-italic / font-size for every run, while underline,
 * strikethrough, superscript/subscript and paragraph structure still come from the clipboard.
 *
 * With a forced family but no `forceFontId`, bold and italic are read from the source and resolved
 * to a variant **within the forced family**, so bold Times New Roman arrives as bold in the
 * destination family rather than as its regular weight.
 *
 * Omit any of them to keep the clipboard's own value. Deliberately independent options rather than
 * one "match destination" flag: they are separable, and `forceFontFamilyId` predates the others.
 *
 * **Leading and tracking are NOT normalised by these**, so a source with its own `line-height` or
 * `letter-spacing` still brings them along. That is the literal reading of an AC that names only
 * "font style and size"; widening it is a product decision, not a code one.
 *
 * `options.onUnresolvedFamily` is called with the raw CSS `font-family` value of every run whose
 * stack contained no permissible family, so the caller can tell the user their typeface was
 * dropped instead of letting the caret-font fallback pass unremarked (WMPCM-9199). It can fire many
 * times for one paste — callers should dedupe.
 *
 * Returns `null` when the HTML is empty, unparseable, or yields no text content.
 */
export function parseExternalClipboardHtml(
  html: string,
  baseStyle: TextRunStyle,
  fonts?: ParagraphBlockFontLookup,
  options?: ExternalPasteOptions
): string | null {
  if (!html.trim()) return null

  // Chrome embeds <!--StartFragment--> / <!--EndFragment--> markers in copied HTML to denote the
  // actual selection. Extract only that slice so ancestor elements and out-of-selection content
  // (nav, headers, adjacent paragraphs) are not included in the paste.
  //
  // The markers are shared constants, not literals, so this parser and `parseStyledRunsFromHtml`
  // cannot drift on what counts as the payload (WMPCM-10131).
  const fragStart = html.indexOf(CLIPBOARD_FRAGMENT_START)
  const fragEnd = html.indexOf(CLIPBOARD_FRAGMENT_END)
  const source =
    fragStart !== -1 && fragEnd > fragStart
      ? html.slice(fragStart + CLIPBOARD_FRAGMENT_START.length, fragEnd)
      : html

  let parsed: Document

  try {
    parsed = new DOMParser().parseFromString(source, 'text/html')
  } catch {
    return null
  }

  const { body } = parsed

  if (!body) return null

  const initAccum: StyleAccum = {
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    pointSize: undefined,
    letterSpacingEm: undefined,
    lineHeight: undefined,
    fontFamilyCssName: undefined,
    verticalAlign: undefined,
  }

  const paragraphs: ParagraphItem[][] = [[]]

  walkNode(body, initAccum, paragraphs)

  // Drop purely-structural leading/trailing empty paragraphs — the walk always starts with one
  // empty "current" paragraph and seals every block boundary with a fresh empty tail — while
  // preserving genuinely blank paragraphs in the interior (e.g. <p>A</p><p></p><p>B</p>).
  let start = 0
  let end = paragraphs.length

  while (start < end && paragraphs[start]!.length === 0) start++
  while (end > start && paragraphs[end - 1]!.length === 0) end--

  const surviving = paragraphs.slice(start, end)

  if (surviving.length === 0) return null

  const hasText = surviving.some((para) =>
    para.some((item) => !('softBreak' in item) && item.text.trim().length > 0)
  )

  if (!hasText) return null

  const wrapper = document.createElement('div')

  for (const para of surviving) {
    const paraDiv = document.createElement('div')

    paraDiv.setAttribute(PASTE_PARA_ATTR, '1')

    if (para.length === 0) {
      paraDiv.appendChild(createRunSpan({ ...baseStyle, textContent: ' ' }, fonts))
    } else {
      for (const item of para) {
        const run =
          'softBreak' in item
            ? resolveRun(initAccum, '\n', baseStyle, fonts, options)
            : resolveRun(item.style, item.text, baseStyle, fonts, options)

        paraDiv.appendChild(createRunSpan(run, fonts))
      }
    }

    wrapper.appendChild(paraDiv)
  }

  return wrapper.innerHTML || null
}
